import { Razor } from "../razor/razor";

export class AuxiliumComponent extends HTMLElement {
	#ObservableBooleans = ["disabled"];

	constructor() {
		super();

		this.init = new WeakMap;
		this.queue = [];
		this._onparsed = null;
		this.parsed = false;
		this.setQueue = [];

		this.disabled = this.hasAttribute("disabled");
		this.badge = this.getAttribute("badge");
		this.tooltip = this.getAttribute("tooltip");
		this.tooltipPlacement = this.getAttribute("tooltipPlacement");

		this.normalizeContent = true;
		this.enhanceContents = this.hasAttribute("enhance");

		const observable = this.constructor.observedAttributes ?? [];
		observable.push("disabled", "tooltip", "tooltip-placement");

		for (const attribute of observable) {
			let value = this.getAttribute(attribute),
				propertyName = attribute.replace(/-([a-z]|\d)/ig, (_, letter) => letter.toUpperCase());

			// The regex converts something like 'button-style' to 'buttonStyle'
			Object.defineProperty(this, propertyName, {
				get() {
					return this.#ObservableBooleans.includes(attribute) ? this.hasAttribute(attribute) : value;
				},
				set(val) {
					if ([true, false].includes(val) && !this.#ObservableBooleans.includes(attribute)) {
						this.#ObservableBooleans.push(attribute);
					}

					const setValue = () => {
						if (val === false && this.hasAttribute(attribute)) {
							this.removeAttribute(attribute);
						} else if (val === true && !this.hasAttribute(attribute)) {
							this.setAttribute(attribute, "");
						} else if (!this.#attributeEquals(attribute, val) && ![true, false].includes(val)) {
							if (val?.toString().trim() === "") { this.removeAttribute(attribute); }
							else { this.setAttribute(attribute, val); }
						}

						if (attribute === "tooltip") {
							const existing = document.querySelector(`#${this.__tooltipID} .tooltip-content`);

							if (existing) {
								if (val) {
									existing.textContent = val;
								} else {
									existing.parentElement.remove();
									this.onmouseover = null;
								}
							} else {
								Razor.RefreshTooltips();
							}
						}
					}

					value = val;

					if (!this.parsed) {
						this.setQueue.push(setValue);
					} else {
						setValue();
					}
				}
			});
		}
	}

	GetValidationMessage(control) {
		if (!control || control.validity.valid) { return this.VALIDATION_CUSTOM; }

		if (control.validity.valueMissing) { return this.VALIDATION_REQUIRED || control.validationMessage; }
		else if (control.validity.typeMismatch) { return this.VALIDATION_TYPE_MISMATCH || control.validationMessage; }
		else if (control.validity.patternMismatch) { return this.VALIDATION_PATTERN_MISMATCH || control.validationMessage; }
		else if (control.validity.tooShort) { return this.VALIDATION_TOO_SHORT || control.validationMessage; }
		else if (control.validity.tooLong) { return this.VALIDATION_TOO_LONG || control.validationMessage; }
		else if (control.validity.rangeUnderflow) { return this.VALIDATION_RANGE_UNDERFLOW || control.validationMessage; }
		else if (control.validity.rangeOverflow) { return this.VALIDATION_RANGE_OVERFLOW || control.validationMessage; }
		else if (control.validity.stepMismatch) { return this.VALIDATION_STEP_MISMATCH || control.validationMessage; }
		else if (control.validity.badInput) { return this.VALIDATION_BAD_INPUT || control.validationMessage; }

		return control.validationMessage;
	}

	InitializeValidationMessages() {
		Object.assign(this, {
			VALIDATION_REQUIRED: this.getAttribute("validation-required") ?? "",
			VALIDATION_TYPE_MISMATCH: this.getAttribute("validation-wrong-type") ?? "",
			VALIDATION_PATTERN_MISMATCH: this.getAttribute("validation-wrong-pattern") ?? "",
			VALIDATION_TOO_SHORT: this.getAttribute("validation-too-short") ?? "",
			VALIDATION_TOO_LONG: this.getAttribute("validation-too-long") ?? "",
			VALIDATION_RANGE_OVERFLOW: this.getAttribute("validation-too-high") ?? "",
			VALIDATION_RANGE_UNDERFLOW: this.getAttribute("validation-too-low") ?? "",
			VALIDATION_STEP_MISMATCH: this.getAttribute("validation-wrong-step") ?? "",
			VALIDATION_BAD_INPUT: this.getAttribute("validation-bad-input") ?? "",
			VALIDATION_CUSTOM: this.getAttribute("validation-custom") ?? ""
		})
	}

	get onparsed() {
		return this._onparsed;
	}

	set onparsed(value) {
		if (this.parsed) { value?.(); }
		else { this._onparsed = value; }
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (!this.parsed) { return; }

		this.attributeChanged?.(name, oldValue, newValue);
	}

	/**
	 * For a boolean attribute, this is a helper function to check whether the attribute should exist or not
	 * @param {*} value The value to check
	 * @return {boolean} True if the attribute is to exist
	 */
	IsAttributeValueTrue(value) {
		return [true, ""].includes(value);
	}

	#attributeEquals(attributeName, value) {
		const attrValue = this.getAttribute(attributeName);

		if (attrValue?.toString() === value?.toString()) { return true; }

		return attrValue == null && ["null", "undefined"].includes(value?.toString());
	}

	raiseEvent(eventName, data = {}) {
		const handler = this[`on${eventName.toLowerCase()}`];

		if (handler) {
			handler(data);
		} else {
			this.dispatchEvent(new Event(eventName, {
				view: window,
				bubbles: false,
				cancelable: false
			}));
		}
	}

	raiseEvents(...events) {
		events.forEach(event => this.raiseEvent(event));
	}

	toggleReadonly(setToTrue = false) {
		this.readonly = setToTrue;
	}

	click() {
		this.raiseEvent("click");
	}

	disconnectedCallback() {
		document.getElementById(this.__tooltipID)?.remove();
	}

	#processQueueItem(element, fnName) {
		if (this.init.get(element) !== true && !this.parsed) {
			this.init.set(element, true);

			if (!this.innerHTML?.trim()) { this.innerHTML = ""; }
			if (this.normalizeContent && this.firstChild?.nodeType === Node.TEXT_NODE) { this.firstChild.trimBreaks(); }
			if (this.enhanceContents) { this.enhance(); }

			element[fnName]?.();
			this.parsed = true;

			this.setQueue.forEach(fn => fn());

			if (this.tooltip || this.querySelector("[tooltip]")) {
				Razor.RefreshTooltips();
			}

			setTimeout(() => {
				this.raiseEvent("parsed");
			}, 0);
		}
	}

	connectedCallback() {
		const parsedCallback = el => {
			this.queue = this.queue.filter(([_, value]) => value !== "parsedCallback");

			if (!this.queue.length) {
				requestAnimationFrame(() => {
					this.queue.splice(0).forEach(([element, fnName]) => this.#processQueueItem(element, fnName));
				});
			}

			this.queue.push([el, "parsedCallback"]);
		}

		const isParsed = el => {
			do {
				if (el.nextSibling) {
					return true;
				}
			} while (el = el.parentNode);

			return false;
		}

		const cleanUp = (el, observer, ownerDocument, onDCL) => {
			observer.disconnect();
			ownerDocument.removeEventListener("DOMContentLoaded", onDCL);

			parsedCallback(el);
		}

		const self = this;
		const { ownerDocument } = self;

		this.init.set(self, false);

		if (ownerDocument.readyState === 'complete' || isParsed(self)) {
			parsedCallback(self);
		}
		else {
			const onDCL = () => cleanUp(self, observer, ownerDocument, onDCL);
			ownerDocument.addEventListener("DOMContentLoaded", onDCL);

			const observer = new MutationObserver(() => {
				if (isParsed(self)) {
					cleanUp(self, observer, ownerDocument, onDCL);
				}
			});

			observer.observe(self.parentNode, { childList: true, subtree: true });
		}
	}

	InitializeCheckboxProperties() {
		this.identifier = this.getAttribute("identifier") ?? crypto.randomUUID().slice(0, 8);
		this.label = this.getAttribute("label");
		this.checked = this.hasAttribute("checked");
		this.name = this.getAttribute("name");
		this.required = this.hasAttribute("required");
		this.helperText = this.getAttribute("helper-text") ?? "";
		this.value = this.getAttribute("value") ?? "true";
	}
}